import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { LoadingSpinner } from '@/shared/components';
import { upsellingModal } from '@/shared/components/UpsellingModal';
import { useUserPlanManager } from '@/shared/plan';
import { DialogBody, DialogFooter, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils';
import { LoadingWorksheetPreviewContext, PaginationContext, WorksheetAccessContext, WorksheetSettingContext } from './context';
import { PageIndicator } from './PageIndicator';
import { QuizWorksheetAdditionalSetting } from './QuizWorksheetAdditionalSetting';
import { QuizWorksheetBasicSetting } from './QuizWorksheetBasicSetting';
import { QuizWorksheetModalAction } from './QuizWorksheetModalAction';
import { QuizWorksheetPDFView } from './QuizWorksheetPDF';
import { type QuizWorksheetExportProps } from './type';
const QuizWorksheetExportModalView = ({
  onClose
}: {
  onClose: () => void;
}) => {
  const [isSavingPDF, setIsSavingPDF] = useState(false);
  const {
    userPlanManager
  } = useUserPlanManager();
  const {
    t
  } = useTranslation(['common']);
  const handleSavePDFSuccess = () => {
    setIsSavingPDF(false);
    onClose();
  };
  const handleSavePDFFail = () => {
    setIsSavingPDF(false);
    const featurePlanType = userPlanManager.getFeaturePlanType('WORKSHEET');
    upsellingModal.open({
      initialPlanType: featurePlanType,
      from: 'quizWorksheet'
    });
  };
  const handleConfirm = () => {
    setIsSavingPDF(true);
  };
  return <ResponsiveDialog className={cn('no-scrollbar', 'mobile-lg:max-h-[720px] !max-w-[1200px] mobile-lg:w-[calc(100vw-80px)] mobile-lg:overflow-scroll', 'max-mobile-lg:w-full')} size="2xl" onClose={onClose} shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="QuizWorksheetExportModalView" data-sentry-source-file="QuizWorksheetExportModal.tsx">
      {isSavingPDF && <SavingSpinner />}

      <DialogHeader title={t('common:quizWorksheet.label')} onClose={onClose} hasCloseButton={true} data-sentry-element="DialogHeader" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
      <DialogBody className="no-scrollbar overflow-scroll" data-sentry-element="DialogBody" data-sentry-source-file="QuizWorksheetExportModal.tsx">
        <div className="relative flex max-mobile-lg:flex-col">
          <div className="no-scrollbar relative overflow-x-scroll">
            <PageIndicator data-sentry-element="PageIndicator" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
            <QuizWorksheetPDFView data-sentry-element="QuizWorksheetPDFView" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
          </div>
          <aside className="flex shrink-0 grow">
            <div className="flex grow flex-col justify-between gap-lg px-lg pb-lg pt-md">
              <QuizWorksheetBasicSetting data-sentry-element="QuizWorksheetBasicSetting" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
              <Divider data-sentry-element="Divider" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
              <QuizWorksheetAdditionalSetting data-sentry-element="QuizWorksheetAdditionalSetting" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
              <QuizWorksheetModalAction className="max-mobile-lg:hidden" onCancel={onClose} onConfirm={handleConfirm} onSaveSuccess={handleSavePDFSuccess} onSaveFail={handleSavePDFFail} data-sentry-element="QuizWorksheetModalAction" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
            </div>
          </aside>
        </div>
      </DialogBody>
      <DialogFooter className={cn('shadow-reversed-sm bg-white', 'max-mobile-lg:py-xs', 'mobile-lg:hidden')} data-sentry-element="DialogFooter" data-sentry-source-file="QuizWorksheetExportModal.tsx">
        <QuizWorksheetModalAction className="flex flex-row justify-center" onCancel={onClose} onConfirm={handleConfirm} onSaveSuccess={handleSavePDFSuccess} onSaveFail={handleSavePDFFail} data-sentry-element="QuizWorksheetModalAction" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
      </DialogFooter>
    </ResponsiveDialog>;
};
const QuizWorksheetExportModalComponent = ({
  props,
  onClose
}: {
  props: QuizWorksheetExportProps;
  onClose: () => void;
}) => {
  const {
    userPlanManager
  } = useUserPlanManager();
  const isPro = userPlanManager.canAccessFeature('WORKSHEET');
  return <WorksheetAccessContext isPro={isPro} data-sentry-element="WorksheetAccessContext" data-sentry-component="QuizWorksheetExportModalComponent" data-sentry-source-file="QuizWorksheetExportModal.tsx">
      <WorksheetSettingContext props={props} data-sentry-element="WorksheetSettingContext" data-sentry-source-file="QuizWorksheetExportModal.tsx">
        <LoadingWorksheetPreviewContext data-sentry-element="LoadingWorksheetPreviewContext" data-sentry-source-file="QuizWorksheetExportModal.tsx">
          <PaginationContext data-sentry-element="PaginationContext" data-sentry-source-file="QuizWorksheetExportModal.tsx">
            <QuizWorksheetExportModalView onClose={onClose} data-sentry-element="QuizWorksheetExportModalView" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
          </PaginationContext>
        </LoadingWorksheetPreviewContext>
      </WorksheetSettingContext>
    </WorksheetAccessContext>;
};
export const QuizWorksheetExportModal = {
  open: async (props: QuizWorksheetExportProps) => {
    logger.track('quiz_worksheet_modal_open');
    return overlay.openAsync(({
      unmount
    }) => {
      return <QuizWorksheetExportModalComponent props={props} onClose={unmount} />;
    });
  }
};
const Divider = () => {
  return <div className="w-full border-t-[1px] border-assistive" data-sentry-component="Divider" data-sentry-source-file="QuizWorksheetExportModal.tsx"></div>;
};
const SavingSpinner = () => {
  return <div className="absolute inset-0 z-50 flex items-center justify-center bg-opacity-black-4" data-sentry-component="SavingSpinner" data-sentry-source-file="QuizWorksheetExportModal.tsx">
      <div className="flex flex-col items-center gap-xs rounded-sm bg-opacity-white-8 px-xl py-lg">
        <LoadingSpinner className="size-2xl text-primary" data-sentry-element="LoadingSpinner" data-sentry-source-file="QuizWorksheetExportModal.tsx" />
        <span className="text-body-lg-bold text-alternative">{'LOADING'}</span>
      </div>
    </div>;
};